import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { Mail, Search, RefreshCw, CheckCircle2, XCircle } from "lucide-react";

export default function AdminEmailLogs() {
  const [logs, setLogs] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/email-logs").then((r) => setLogs(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = logs.filter((l) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [l.to, l.po_number, l.client_name, l.driver_email, l.email_id, l.subject, l.status]
      .filter(Boolean).some((x) => String(x).toLowerCase().includes(s));
  });

  const sentCount = logs.filter((l) => l.status === "sent").length;
  const failCount = logs.filter((l) => l.status === "failed").length;

  return (
    <Layout>
      <div className="p-6 md:p-10 space-y-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="label-caps text-muted-foreground mb-2">Traçabilité</div>
            <h1 className="font-display font-black text-4xl md:text-5xl leading-none tracking-tighter">Journal des envois</h1>
            <p className="text-muted-foreground mt-2">Qui a reçu quel PDF, quand et avec quel statut.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Card className="rounded-none border-2 px-4 py-2 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-primary" />
              <span className="label-caps text-muted-foreground">Livrés</span>
              <span className="font-display font-black text-xl">{sentCount}</span>
            </Card>
            <Card className="rounded-none border-2 px-4 py-2 flex items-center gap-2">
              <XCircle className="w-4 h-4 text-destructive" />
              <span className="label-caps text-muted-foreground">Échecs</span>
              <span className="font-display font-black text-xl">{failCount}</span>
            </Card>
            <Button
              data-testid="email-logs-refresh"
              variant="outline"
              className="rounded-none border-2 h-11"
              onClick={load}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Rafraîchir
            </Button>
          </div>
        </div>

        <Card className="rounded-none border-2 overflow-hidden">
          <div className="p-4 border-b-2 border-border flex flex-col md:flex-row md:items-center gap-3 justify-between">
            <h2 className="font-display font-black text-xl flex items-center gap-2">
              <Mail className="w-5 h-5" /> Derniers courriels
            </h2>
            <div className="relative w-full md:w-96">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="email-logs-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Rechercher destinataire, P.O., client, ID…"
                className="h-11 pl-10 border-2 rounded-none"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b-2">
                  <TableHead className="label-caps">Envoyé le</TableHead>
                  <TableHead className="label-caps">Destinataire</TableHead>
                  <TableHead className="label-caps">Bon de transport</TableHead>
                  <TableHead className="label-caps">Client</TableHead>
                  <TableHead className="label-caps">ID courriel</TableHead>
                  <TableHead className="label-caps">Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                      Aucun envoi pour l'instant.
                    </TableCell>
                  </TableRow>
                )}
                {filtered.map((l) => (
                  <TableRow key={l.id} data-testid={`log-row-${l.id}`} className="hover:bg-secondary/50">
                    <TableCell className="font-mono-alt text-xs whitespace-nowrap">
                      {l.sent_at ? new Date(l.sent_at).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" }) : "—"}
                    </TableCell>
                    <TableCell className="font-semibold">{l.to}</TableCell>
                    <TableCell className="font-mono-alt text-xs">{l.po_number ? `P.O. ${l.po_number}` : `#${(l.transport_id||"").slice(-6).toUpperCase()}`}</TableCell>
                    <TableCell>{l.client_name || "—"}</TableCell>
                    <TableCell className="font-mono-alt text-xs text-muted-foreground truncate max-w-[200px]" title={l.email_id}>
                      {l.email_id || "—"}
                    </TableCell>
                    <TableCell>
                      {l.status === "sent" ? (
                        <Badge className="rounded-none bg-black text-white">Livré</Badge>
                      ) : (
                        <Badge className="rounded-none bg-destructive text-destructive-foreground" title={l.error}>
                          Échec
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>

        <p className="text-xs text-muted-foreground">
          Note : « Livré » signifie que le fournisseur courriel a accepté le message (HTTP 202). Le destinataire peut encore le voir en spam — invitez-le à ajouter le domaine expéditeur à ses contacts sûrs.
        </p>
      </div>
    </Layout>
  );
}
